import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity, AlertTriangle, ArrowRight, CheckCircle2, ChevronRight, Clock3, Database,
  FileImage, Image, LayoutDashboard, ListTree, Loader2, LockKeyhole, LogOut, Phone,
  RefreshCw, Search, Send, ShieldCheck, ShoppingBag, Timer, Upload, UserRound, X,
} from 'lucide-react'
import {
  clearSession, exchangeSso, fetchDashboard, fetchDashboardDetail, fetchPublicConfig, getSsoLoginUrl, getStoredUser,
  logout, normalizeCustomerPhone, refreshSession, submitVerification,
  type DashboardDetail, type DashboardPayload, type ProcessingRun, type SessionUser,
  type VerificationProgress, type VerificationResult,
} from './api'
import { isRetryableVerificationStatus, resolveWarrantyJourneyStage, warrantyJourneyCopy } from './warrantyJourney'
import { filterProcessingRuns } from './adminDashboard'
import {
  adminReasonMessage, adminReasonTitle, displayProcessingMessage, matchMethodLabels,
  processingOutcomeLabels, processingStageLabels, registrationStatusLabels,
} from './warrantyCopy'

type TurnstileAPI = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string
  remove: (widgetId: string) => void
}
declare global { interface Window { turnstile?: TurnstileAPI } }

function TurnstileBox({ siteKey, onToken }: { siteKey: string; onToken: (token: string) => void }) {
  const host = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!siteKey || !host.current) return
    let disposed = false
    let widgetId = ''
    const render = () => {
      if (disposed || !host.current || !window.turnstile || widgetId) return
      widgetId = window.turnstile.render(host.current, {
        sitekey: siteKey, theme: 'dark', language: 'vi', size: 'flexible',
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      })
    }
    if (window.turnstile) render()
    else {
      let script = document.querySelector<HTMLScriptElement>('script[data-warranty-turnstile]')
      if (!script) {
        script = document.createElement('script')
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
        script.async = true; script.defer = true; script.dataset.warrantyTurnstile = 'true'
        document.head.appendChild(script)
      }
      script.addEventListener('load', render, { once: true })
    }
    return () => { disposed = true; if (widgetId && window.turnstile) window.turnstile.remove(widgetId) }
  }, [siteKey, onToken])
  return <div className="turnstile-box" ref={host} aria-label="Xác minh chống spam" />
}

function formatDate(value?: string) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('vi-VN', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}
function warrantySessionId() {
  const key = 'gearvn_warranty_session'
  const value = crypto.randomUUID()
  sessionStorage.setItem(key, value)
  return value
}

type ChatMessage =
  | { id: string; role: 'customer'; kind: 'file'; fileName: string; fileSize: number }
  | { id: string; role: 'customer' | 'assistant'; kind: 'text'; text: string; tone?: 'success' | 'review' | 'retry' }

function chatMessageId() { return crypto.randomUUID() }

function RegistrationPage() {
  const [file, setFile] = useState<File | null>(null)
  const [phoneDraft, setPhoneDraft] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileCycle, setTurnstileCycle] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [retryPending, setRetryPending] = useState(false)
  const [finalResult, setFinalResult] = useState<VerificationResult | null>(null)
  const [progress, setProgress] = useState<VerificationProgress[]>([])
  const [elapsedMs, setElapsedMs] = useState(0)
  const [sessionKey, setSessionKey] = useState(() => warrantySessionId())
  const attemptStartedAt = useRef(Date.now())
  const chatBody = useRef<HTMLDivElement>(null)

  const stage = resolveWarrantyJourneyStage({
    hasFile: Boolean(file), hasPhone: Boolean(customerPhone), busy, retryPending, completed: Boolean(finalResult),
  })
  const journeySteps = [
    { label: 'Ảnh đơn', state: finalResult ? 'done' : stage === 'image' || stage === 'retry_image' ? 'active' : 'done' },
    { label: 'Số điện thoại', state: finalResult || customerPhone ? 'done' : stage === 'phone' ? 'active' : 'pending' },
    { label: 'Xác thực', state: finalResult ? 'done' : stage === 'ready' || stage === 'processing' ? 'active' : 'pending' },
  ]

  useEffect(() => { void fetchPublicConfig().then((config) => setTurnstileSiteKey(config.turnstileSiteKey)).catch(() => undefined) }, [])
  useEffect(() => {
    if (!chatBody.current) return
    chatBody.current.scrollTop = chatBody.current.scrollHeight
  }, [messages.length, progress.length, busy, error])

  function chooseFile(next: File | null) {
    if (!next) return
    setFile(next); setError(''); setProgress([]); setElapsedMs(0); setRetryPending(false)
    attemptStartedAt.current = Date.now()
    setMessages((current) => [
      ...current,
      { id: chatMessageId(), role: 'customer', kind: 'file', fileName: next.name, fileSize: next.size },
      ...(customerPhone ? [{ id: chatMessageId(), role: 'assistant' as const, kind: 'text' as const, text: 'Đã nhận ảnh mới. Số điện thoại vẫn được giữ trong phiên này; bạn có thể xác thực tiếp.' }] : []),
    ])
  }

  function acceptPhone() {
    const normalized = normalizeCustomerPhone(phoneDraft)
    if (!normalized) { setError('Số điện thoại cần đủ 10–11 số và bắt đầu bằng 0.'); return }
    setCustomerPhone(normalized); setPhoneDraft(normalized); setError('')
    setMessages((current) => [
      ...current,
      { id: chatMessageId(), role: 'customer', kind: 'text', text: `SĐT nhận hàng: ${normalized.slice(0, 3)} •••• ${normalized.slice(-3)}` },
      { id: chatMessageId(), role: 'assistant', kind: 'text', text: 'Đã nhận số điện thoại. Thông tin đã đủ, bạn có thể hoàn tất đăng ký ngay.' },
    ])
  }

  async function submit() {
    if (!file) { setError('Vui lòng chọn ảnh chụp chi tiết đơn hàng.'); return }
    if (!customerPhone) { setError('Vui lòng nhập số điện thoại nhận hàng.'); return }
    if (turnstileSiteKey && !turnstileToken) { setError('Vui lòng hoàn tất bước chống spam.'); return }
    setBusy(true); setError(''); setProgress([]); setElapsedMs(0)
    try {
      const result = await submitVerification(
        { file, platform: '', imei: '', customerPhone, sessionId: sessionKey, startedAt: attemptStartedAt.current, turnstileToken },
        (event) => {
          setElapsedMs(event.elapsedMs)
          if (event.type !== 'progress') return
          setProgress((current) => {
            const next = current.filter((item) => item.step !== event.step)
            return [...next, event]
          })
        },
      )
      setElapsedMs(result.elapsedMs || 0)
      if (isRetryableVerificationStatus(result.status)) {
        setMessages((current) => [...current, {
          id: chatMessageId(), role: 'assistant', kind: 'text', tone: 'retry',
          text: `${result.message}\nBạn gửi ảnh khác ngay bên dưới nhé. Số điện thoại và phiên đăng ký vẫn được giữ nguyên.`,
        }])
        setFile(null); setRetryPending(true); setProgress([])
      } else {
        setMessages((current) => [...current, {
          id: chatMessageId(), role: 'assistant', kind: 'text',
          tone: result.status === 'verified' ? 'success' : 'review',
          text: `${result.message || 'GEARVN đã tiếp nhận yêu cầu của bạn.'}\nHoàn tất trong ${((result.elapsedMs || 0) / 1000).toFixed(1)} giây.`,
        }])
        setFinalResult(result)
      }
    } catch (err) { setError(err instanceof Error ? err.message : String(err)) }
    finally {
      setBusy(false)
      if (turnstileSiteKey) { setTurnstileToken(''); setTurnstileCycle((value) => value + 1) }
    }
  }

  function startNewRegistration() {
    setFile(null); setPhoneDraft(''); setCustomerPhone('')
    setError(''); setMessages([]); setRetryPending(false); setFinalResult(null); setProgress([]); setElapsedMs(0)
    setTurnstileToken(''); setTurnstileCycle((value) => value + 1)
    setSessionKey(warrantySessionId()); attemptStartedAt.current = Date.now()
  }

  return (
    <main className="public-shell">
      <nav className="brand-bar">
        <a className="gearvn-logo" href="/" aria-label="GEARVN"><img src="/gearvn-logo-white.svg" alt="" /></a>
        <a className="admin-link" href="/admin"><LockKeyhole /> Quản trị</a>
      </nav>
      <section className="registration-layout">
        <div className="public-copy">
          <span className="red-kicker">Đăng ký bảo hành online</span>
          <h1>Mua hàng qua sàn.<br />Bảo hành trọn vẹn tại GEARVN.</h1>
          <p className="hero-lead">Đăng ký thông tin đơn Shopee hoặc TikTok Shop để GEARVN hỗ trợ nhanh chóng khi bạn cần bảo hành. Chỉ cần ảnh chi tiết đơn hàng và số điện thoại nhận hàng.</p>
          <div className="policy-assurance"><ShieldCheck /><div><strong>Quyền lợi bảo hành đầy đủ</strong><span>Đơn hàng của bạn được GEARVN tiếp nhận theo chính sách bảo hành hiện hành.</span></div></div>
          <div className="trust-grid">
            <span><CheckCircle2 /> Đăng ký đơn giản</span>
            <span><Clock3 /> Hoàn tất trong ít phút</span>
            <span><ShoppingBag /> Shopee & TikTok Shop</span>
          </div>
        </div>
        <section className="chat-card" aria-label="Đăng ký bảo hành">
          <header>
            <span className="agent-avatar">G</span>
            <div><strong>Trợ lý bảo hành GEARVN</strong><small>{warrantyJourneyCopy[stage]}</small></div>
            <i aria-hidden="true" />
          </header>
          <div className="journey-steps" aria-label="Tiến trình đăng ký">
            {journeySteps.map((item, index) => <div className={`journey-step ${item.state}`} key={item.label}>
              <span>{item.state === 'done' ? <CheckCircle2 /> : index + 1}</span><small>{item.label}</small>
            </div>)}
          </div>
          <div className="chat-body" ref={chatBody}>
            <div className="bubble assistant">Chào bạn! Gửi ảnh trang chi tiết đơn hàng có mã đơn và thông tin nhận hàng để bắt đầu đăng ký. Bạn có thể che các thông tin không liên quan.</div>
            {messages.map((message) => message.kind === 'file'
              ? <div className="bubble customer file-bubble" key={message.id}><FileImage /><span><strong>{message.fileName}</strong><small>{(message.fileSize / 1024 / 1024).toFixed(1)} MB</small></span></div>
              : <div className={`bubble ${message.role}${message.tone ? ` ${message.tone}` : ''}`} key={message.id}>
                {message.tone === 'success' && <CheckCircle2 />}{message.tone === 'retry' && <RefreshCw />}
                <span>{message.text}</span>
              </div>)}
            {file && !customerPhone && <div className="bubble assistant"><Phone /> <span>TikTok Shop và Shopee thường che số điện thoại trong ảnh. Bạn nhập giúp số điện thoại nhận hàng đầy đủ để GEARVN xác thực nhé.</span></div>}
            {busy && progress.length > 0 && <div className="bubble assistant progress-bubble" aria-live="polite">
              <strong>Tiến trình xác thực</strong>
              <div className="progress-list">{progress.map((item) => <div className={`progress-row ${item.status}`} key={item.step}>
                {item.status === 'done' ? <CheckCircle2 /> : <Loader2 className="spin" />}
                <span>{item.message}</span>
              </div>)}</div>
              <small>Đã xử lý {(elapsedMs / 1000).toFixed(1)} giây</small>
            </div>}
            {error && <div className="bubble error" role="alert">{error}</div>}
          </div>
          {!file && !finalResult && <label className={`upload-zone${retryPending ? ' retry' : ''}`}>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => chooseFile(event.target.files?.[0] || null)} />
            {retryPending ? <RefreshCw /> : <Upload />}<span><strong>{retryPending ? 'Gửi ảnh khác trong phiên này' : 'Gửi ảnh chi tiết đơn hàng'}</strong><small>{retryPending ? 'Giữ nguyên SĐT · chọn ảnh có dòng “Số đơn hàng”' : 'JPG, PNG hoặc WebP · tối đa 10 MB'}</small></span>
          </label>}
          {file && !customerPhone && <form className="phone-composer" onSubmit={(event) => { event.preventDefault(); acceptPhone() }}>
            <Phone /><input autoFocus inputMode="tel" autoComplete="tel" value={phoneDraft} onChange={(event) => setPhoneDraft(event.target.value)} placeholder="Nhập số điện thoại nhận hàng" aria-label="Số điện thoại nhận hàng" />
            <button type="submit" aria-label="Gửi số điện thoại"><Send /></button>
          </form>}
          {file && customerPhone && !finalResult && <>
            <div className="change-row"><label className="change-file"><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => chooseFile(event.target.files?.[0] || null)} />Đổi ảnh</label><button type="button" onClick={() => { setCustomerPhone(''); setError('') }}>Sửa SĐT</button></div>
            {turnstileSiteKey && <TurnstileBox key={turnstileCycle} siteKey={turnstileSiteKey} onToken={setTurnstileToken} />}
            <button className="primary-action" onClick={() => void submit()} disabled={busy || (Boolean(turnstileSiteKey) && !turnstileToken)}>
              {busy ? <><Loader2 className="spin" /> Đang kiểm tra thông tin...</> : <>Hoàn tất đăng ký <ArrowRight /></>}
            </button>
          </>}
          {finalResult && <button className="primary-action new-session-action" type="button" onClick={startNewRegistration}><RefreshCw /> Đăng ký bảo hành mới</button>}
          <p className="privacy-copy">Thông tin chỉ dùng để xác thực đơn, hỗ trợ bảo hành và chăm sóc khách hàng GEARVN.</p>
        </section>
      </section>
    </main>
  )
}

function RunPill({ outcome }: { outcome: ProcessingRun['outcome'] }) {
  return <span className={`status ${outcome}`}>{processingOutcomeLabels[outcome]}</span>
}

const pilotFeedbackLabels: Record<string, string> = {
  correct: 'Đúng kỳ vọng',
  incorrect: 'Có bug',
  unclear: 'Chưa rõ',
}

const pilotBugLabels: Record<string, string> = {
  ocr: 'OCR',
  order_matching: 'Khớp đơn',
  phone_matching: 'Khớp SĐT',
  result_copy: 'Nội dung trả về',
  latency: 'Tốc độ',
  system: 'Hệ thống',
  other: 'Khác',
}

function text(value: unknown) { return value == null || value === '' ? '—' : String(value) }
function duration(value: unknown) {
  const ms = Number(value || 0)
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)} giây`
}

function DetailDrawer({ detail, loading, error, onClose }: { detail: DashboardDetail | null; loading: boolean; error: string; onClose: () => void }) {
  const order = detail?.order || {}
  const registration = detail?.registration || {}
  const ocr = registration.ocr_fields || {}
  const reason = String(registration.failure_reason || '')
  const imagePhone = ocr.recipient_phone || ocr.customer_phone
  return <div className="drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <aside className="detail-drawer" role="dialog" aria-modal="true" aria-label="Chi tiết lượt xác thực">
      <header className="drawer-header">
        <div><span className="eyebrow">CHI TIẾT HÀNH TRÌNH</span><h2>{text(registration.public_code || order.platform_order_id || 'Lượt xử lý')}</h2></div>
        <button className="icon-button" onClick={onClose} aria-label="Đóng"><X /></button>
      </header>
      {loading && <div className="loading-state"><Loader2 className="spin" /> Đang tải chi tiết...</div>}
      {error && <div className="dashboard-error">{error}</div>}
      {!loading && detail && <div className="drawer-body">
        {reason && <section className="review-alert"><AlertTriangle /><div><strong>{adminReasonTitle(reason)}</strong><p>{adminReasonMessage(reason, registration.customer_phone, imagePhone)}</p></div></section>}
        <section className="detail-grid">
          <article className="detail-card"><h3><ShoppingBag /> Đơn hàng</h3><dl>
            <div><dt>Kênh</dt><dd>{text(order.platform || registration.platform)}</dd></div>
            <div><dt>Mã đơn sàn</dt><dd className="mono">{text(order.platform_order_id || registration.platform_order_id)}</dd></div>
            <div><dt>Đơn GEARVN</dt><dd className="mono">{text(order.internal_order_id || registration.internal_order_id).toUpperCase()}</dd></div>
            <div><dt>Trạng thái nguồn</dt><dd>{text(order.source_status)}</dd></div>
          </dl></article>
          <article className={`detail-card ${reason.includes('phone') ? 'conflict-card' : ''}`}><h3><UserRound /> Thông tin đối chiếu</h3><dl>
            <div><dt>SĐT khách cung cấp</dt><dd className="mono strong-value">{text(registration.customer_phone)}</dd></div>
            <div><dt>SĐT trên ảnh (đã che)</dt><dd className="mono">{text(imagePhone)}</dd></div>
            <div><dt>Tên người nhận</dt><dd>{text(registration.customer_name || ocr.recipient_name)}</dd></div>
            <div><dt>Địa chỉ</dt><dd>{text(registration.customer_address || ocr.recipient_address)}</dd></div>
          </dl></article>
          <article className="detail-card"><h3><Database /> Kết quả hệ thống</h3><dl>
            <div><dt>Trạng thái</dt><dd>{registrationStatusLabels[String(registration.status || '')] || 'Chưa xác định'}</dd></div>
            <div><dt>Kết quả tìm đơn</dt><dd>{matchMethodLabels[String(registration.match_method || '')] || 'Chưa có mô tả'}</dd></div>
            <div><dt>Mức khớp mã đơn</dt><dd>{registration.match_score == null ? '—' : `${Math.round(Number(registration.match_score) * 100)}%`}</dd></div>
            <div><dt>IMEI</dt><dd className="mono">{text(registration.imei)}</dd></div>
          </dl></article>
          <article className="detail-card"><h3><Image /> Chứng từ</h3><dl>
            <div><dt>Tệp ảnh</dt><dd>{text(registration.image_filename)}</dd></div>
            <div><dt>SHA-256</dt><dd className="mono hash-value">{text(registration.image_sha256)}</dd></div>
            <div><dt>Độ tin cậy OCR</dt><dd>{registration.ocr_confidence == null ? '—' : `${Math.round(Number(registration.ocr_confidence) * 100)}%`}</dd></div>
            <div><dt>Phiên</dt><dd className="mono hash-value">{text(registration.session_key)}</dd></div>
          </dl></article>
        </section>
        <section className="timeline-section"><div className="section-title"><ListTree /><div><h3>Nhật ký các chặng xử lý</h3><p>Mỗi lần khách gửi lại là một lượt riêng, không làm thay đổi audit xác minh.</p></div></div>
          {(detail.runs || []).length === 0 && <div className="empty-state">Lượt cũ chưa có nhật ký theo chặng.</div>}
          {(detail.runs || []).map((run) => {
            const runId = String(run.run_id || '')
            const runEvents = detail.events.filter((event) => event.run_id === runId)
            return <article className="run-timeline" key={runId}>
              <header><div><strong>{formatDate(String(run.started_at || ''))}</strong><small>{run.is_duplicate ? 'Yêu cầu gửi lại' : 'Lượt xử lý mới'}</small></div><span>{duration(run.duration_ms)}</span></header>
              <ol>{runEvents.map((event) => <li key={event.event_id} className={event.status === 'error' ? 'failed' : ''}>
                <span className="timeline-dot">{event.status === 'done' || event.type === 'complete' ? <CheckCircle2 /> : event.status === 'error' ? <AlertTriangle /> : <Clock3 />}</span>
                <div><strong>{processingStageLabels[event.step] || 'Chặng xử lý'}</strong><p>{displayProcessingMessage(event.message)}</p></div><time>+{duration(event.elapsed_ms)}</time>
              </li>)}</ol>
            </article>
          })}
        </section>
        {(detail.feedback || []).length > 0 && <section className="pilot-feedback-section">
          <div className="section-title"><Activity /><div><h3>Phản hồi từ team Sàn</h3><p>Kết quả pilot do tester gửi lại qua MCP gateway.</p></div></div>
          <div className="pilot-feedback-list">{detail.feedback.map((entry, index) => <article className="pilot-feedback-card" key={String(entry.feedback_id || index)}>
            <header><span className={`feedback-pill ${String(entry.verdict || '')}`}>{pilotFeedbackLabels[String(entry.verdict || '')] || text(entry.verdict)}</span><time>{formatDate(String(entry.created_at || ''))}</time></header>
            <dl>
              <div><dt>Tester</dt><dd className="mono">{text(entry.tester_id)}</dd></div>
              <div><dt>Nhóm lỗi</dt><dd>{pilotBugLabels[String(entry.bug_type || '')] || text(entry.bug_type)}</dd></div>
              <div><dt>Kỳ vọng</dt><dd>{text(entry.expected_outcome)}</dd></div>
            </dl>
            {Boolean(entry.note) && <p>{text(entry.note)}</p>}
          </article>)}</div>
        </section>}
      </div>}
    </aside>
  </div>
}

function AdminDashboard({ user }: { user: SessionUser }) {
  const [data, setData] = useState<DashboardPayload | null>(null)
  const [runStatus, setRunStatus] = useState('all')
  const [days, setDays] = useState(7)
  const [platform, setPlatform] = useState('')
  const [sourceChannel, setSourceChannel] = useState('')
  const [feedbackStatus, setFeedbackStatus] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState<DashboardDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const params = useMemo(() => new URLSearchParams({ days: String(days), status: 'all', runStatus, platform, sourceChannel, feedbackStatus, limit: '200' }), [days, runStatus, platform, sourceChannel, feedbackStatus])

  async function load() {
    setLoading(true); setError('')
    try { setData(await fetchDashboard(params)) }
    catch (err) { setError(err instanceof Error ? err.message : String(err)) }
    finally { setLoading(false) }
  }
  useEffect(() => { void load() }, [params.toString()])

  async function openDetail(selection: { sourceOrderId?: string; registrationId?: string; runId?: string }) {
    setDrawerOpen(true); setDetail(null); setDetailError(''); setDetailLoading(true)
    try { setDetail(await fetchDashboardDetail(selection)) }
    catch (err) { setDetailError(err instanceof Error ? err.message : String(err)) }
    finally { setDetailLoading(false) }
  }

  const stats = data?.stats || {}
  const visibleRuns = useMemo(() => filterProcessingRuns(data?.runs || [], search), [data?.runs, search])
  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <a className="gearvn-logo" href="/" aria-label="GEARVN"><img src="/gearvn-logo-white.svg" alt="" /></a>
        <div className="module-name"><LayoutDashboard /> Xác thực bảo hành</div>
        <nav>
          <button className="active" type="button"><Activity /> Yêu cầu bảo hành</button>
        </nav>
        <div className="admin-user"><span>{user.name}</span><small>{user.identity}</small><button onClick={() => void logout()}><LogOut /> Đăng xuất</button></div>
      </aside>
      <main className="dashboard-main">
        <header className="dashboard-header">
          <div><span className="eyebrow">VẬN HÀNH · XÁC THỰC BẢO HÀNH</span><h1>Yêu cầu bảo hành</h1><p>Theo dõi các lượt khách gửi, ưu tiên trường hợp cần kiểm tra và xem toàn bộ hành trình xử lý.</p></div>
          <button className="refresh-button" type="button" onClick={() => void load()} disabled={loading}>{loading ? <Loader2 className="spin" /> : <RefreshCw />} Làm mới</button>
        </header>
        {error && <div className="dashboard-error" role="alert">{error}</div>}
        <section className="kpi-grid">
          <KPI label="Tổng lượt xử lý" value={stats.total_runs} tone="blue" days={days} />
          <KPI label="Đã xác thực" value={stats.completed_runs} tone="green" days={days} />
          <KPI label="Cần nhân viên kiểm tra" value={stats.review_runs} tone="amber" days={days} />
          <KPI label="Gián đoạn" value={stats.interrupted_runs} tone="red" days={days} />
        </section>
        <section className="pilot-kpi-grid" aria-label="Thống kê pilot MCP">
          <KPI label="Lượt pilot MCP" value={stats.pilot_runs} tone="blue" days={days} />
          <KPI label="Chờ team Sàn phản hồi" value={stats.pilot_pending_feedback} tone="amber" days={days} />
          <KPI label="Bug / lệch kỳ vọng" value={stats.pilot_bug_runs} tone="red" days={days} />
        </section>
        <section className="filters-panel">
          <div className="filter-tabs">
            {([['all', 'Tất cả'], ['needs_review', 'Cần nhân viên kiểm tra'], ['verified', 'Đã xác thực'], ['needs_input', 'Cần ảnh khác'], ['no_match', 'Chưa tìm thấy'], ['failed', 'Xử lý gián đoạn'], ['duplicate', 'Đã gửi trước đó']]
            ).map(([value, label]) => <button key={value} className={runStatus === value ? 'active' : ''} onClick={() => setRunStatus(value)}>{label}</button>)}
          </div>
          <select aria-label="Khoảng thời gian" value={days} onChange={(event) => setDays(Number(event.target.value))}><option value="7">7 ngày</option><option value="14">14 ngày</option><option value="30">30 ngày</option></select>
          <select value={platform} onChange={(event) => setPlatform(event.target.value)}><option value="">Tất cả kênh</option><option value="shopee">Shopee</option><option value="tiktok_shop">TikTok Shop</option></select>
          <select value={sourceChannel} onChange={(event) => setSourceChannel(event.target.value)} aria-label="Nguồn gửi"><option value="">Mọi nguồn gửi</option><option value="customer_web">Khách hàng</option><option value="pilot_mcp">Pilot MCP</option></select>
          <select value={feedbackStatus} onChange={(event) => setFeedbackStatus(event.target.value)} aria-label="Phản hồi pilot"><option value="">Mọi phản hồi</option><option value="pending">Chờ phản hồi</option><option value="bug">Bug / lệch kỳ vọng</option><option value="correct">Đúng kỳ vọng</option><option value="unclear">Chưa rõ</option></select>
          <label className="search-box"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Mã đơn / tester / nội dung feedback" /></label>
        </section>
        <section className="table-card">
          {loading ? <div className="loading-state"><Loader2 className="spin" /> Đang tải dữ liệu...</div> : (
            <div className="table-scroll"><table><thead><tr><th>Bắt đầu</th><th>Nguồn / tester</th><th>Kênh / mã đơn</th><th>Đối tượng test</th><th>Các chặng</th><th>Kết quả</th><th>Feedback pilot</th><th>Thời gian xử lý</th><th>Phản hồi cuối</th><th></th></tr></thead>
                <tbody>{visibleRuns.map((run) => <tr className="clickable-row" tabIndex={0} key={run.run_id} onClick={() => void openDetail({ runId: run.run_id })} onKeyDown={(event) => { if (event.key === 'Enter') void openDetail({ runId: run.run_id }) }}>
                  <td>{formatDate(run.started_at)}</td>
                  <td>{run.source_channel === 'pilot_mcp' ? <><span className="source-pill pilot">Pilot MCP</span><strong className="mono tester-id">{run.tester_id || '—'}</strong><small className="client-name">{run.client_name || 'Agent'}</small></> : <span className="source-pill customer">Khách hàng</span>}</td>
                  <td><span className={`channel ${run.platform || ''}`}>{run.platform === 'shopee' ? 'Shopee' : run.platform === 'tiktok_shop' ? 'TikTok Shop' : 'Chưa đọc được'}</span><strong className="mono run-order-id">{run.platform_order_id || '—'}</strong></td>
                  <td className="mono">{run.source_channel === 'pilot_mcp' ? 'Dữ liệu test' : run.customer_phone_masked || '—'}</td>
                  <td><span className="stage-count"><ListTree /> {run.event_count || 0} chặng</span></td>
                  <td><RunPill outcome={run.outcome} />{run.expectation_mismatch && <small className="expectation-warning"><AlertTriangle /> Khác kỳ vọng: {processingOutcomeLabels[run.expected_outcome || 'failed']}</small>}</td>
                  <td>{run.source_channel !== 'pilot_mcp' ? '—' : run.feedback_verdict ? <><span className={`feedback-pill ${run.feedback_verdict}`}>{pilotFeedbackLabels[run.feedback_verdict]}</span>{run.feedback_bug_type && <small className="feedback-bug">{pilotBugLabels[run.feedback_bug_type]}</small>}</> : <span className="feedback-pill pending">Chờ phản hồi</span>}</td>
                  <td><span className="duration-cell"><Timer /> {duration(run.duration_ms)}</span></td>
                  <td className="product-cell">{displayProcessingMessage(run.current_message)}</td>
                  <td><ChevronRight className="row-chevron" /></td>
                </tr>)}{visibleRuns.length === 0 && <tr><td colSpan={10}><div className="empty-state">Không có lượt xử lý phù hợp với bộ lọc hiện tại.</div></td></tr>}</tbody></table></div>
          )}
        </section>
        <p className="dashboard-footnote">Pilot MCP chỉ hiển thị mã tester và dữ liệu test đã làm sạch; không trả dữ liệu DWH nhạy cảm về agent. Chi tiết khách hàng chỉ mở sau SSO quản trị.</p>
      </main>
      {drawerOpen && <DetailDrawer detail={detail} loading={detailLoading} error={detailError} onClose={() => setDrawerOpen(false)} />}
    </div>
  )
}

function KPI({ label, value, tone, days }: { label: string; value: unknown; tone: string; days: number }) {
  return <div className={`kpi-card ${tone}`}><small>{label}</small><strong>{Number(value || 0).toLocaleString('vi-VN')}</strong><span>{days} ngày gần nhất</span></div>
}

function AdminGate() {
  const [user, setUser] = useState<SessionUser | null>(getStoredUser())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.pathname === '/auth/sso/callback') {
      const code = url.searchParams.get('ssoToken') || url.searchParams.get('code') || ''
      exchangeSso(code).then((next) => { window.history.replaceState({}, '', '/admin'); setUser(next) }).catch((err) => setError(String(err))).finally(() => setLoading(false))
    } else {
      refreshSession().then(setUser).catch((err) => setError(err instanceof Error ? err.message : String(err))).finally(() => setLoading(false))
    }
    const refresh = () => { if (getStoredUser()) void refreshSession().then(setUser).catch(() => undefined) }
    const interval = window.setInterval(refresh, 30 * 60 * 1000)
    const refreshWhenVisible = () => { if (document.visibilityState === 'visible') refresh() }
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => { window.clearInterval(interval); document.removeEventListener('visibilitychange', refreshWhenVisible) }
  }, [])
  if (loading) return <div className="gate-state"><Loader2 className="spin" /> Đang kiểm tra SSO...</div>
  if (!user || !user.isAdmin) return <div className="gate-state"><LockKeyhole /><h1>Dashboard nội bộ GEARVN</h1><p>{error || 'Đăng nhập SSO bằng tài khoản được cấp quyền quản trị.'}</p><a className="primary-action" href={getSsoLoginUrl()}>Đăng nhập SSO <ArrowRight /></a>{user && <button className="text-button" onClick={clearSession}>Xóa phiên hiện tại</button>}</div>
  return <AdminDashboard user={user} />
}

export function App() {
  const path = window.location.pathname
  return path === '/admin' || path === '/auth/sso/callback' ? <AdminGate /> : <RegistrationPage />
}
